import React from 'react';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { OrganizationVideo } from 'lib/api/organizationVideo/useOrganizationVideoQuery';
import { FormikInputField } from 'lib/components/formik/FormikInputField';
import { Flex } from 'lib/components/styles/layout';
import { ParagraphNormal } from 'lib/components/styles/typography';
import { Button } from 'react-covideo-common';
import { OrganizationVideoDuplicatePlaylistTable } from './OrganizationVideoDuplicatePlaylistTable';
import { useDuplicateOrganizationVideoPlaylistMutation } from 'lib/api/organizationVideo/useDuplicateOrganizationVideoPlaylistMutation';

export const OrganizationVideoDuplicatePlaylistSchema = Yup.object().shape({
  playlist: Yup.string().required('Playlist is required'),

  year: Yup.number()
    .typeError('Year must be a number')
    .required('Year is required'),

  selectedOrganizationVideoIds: Yup.array()
    .of(Yup.number().required())
    .min(1, 'At least one video must be selected')
    .required(),
});

interface OrganizationVideoDuplicatePlaylistFormValues {
  year: number;
  playlist: string;
  selectedOrganizationVideoIds: number[];
}

interface IModalOrganizationDuplicatePlaylistFormProps {
  videos: OrganizationVideo[];
  handleModalClose: () => void;
}

export const ModalOrganizationDuplicatePlaylistForm = ({
  videos,
  handleModalClose,
}: IModalOrganizationDuplicatePlaylistFormProps) => {
  const initialValues: OrganizationVideoDuplicatePlaylistFormValues = {
    year: new Date().getFullYear(),
    playlist: '',
    selectedOrganizationVideoIds: videos.map(v => v.organizationVideoId),
  };

  const { mutate: duplicatePlaylistMutation } =
    useDuplicateOrganizationVideoPlaylistMutation({
      onSuccessCallback: handleModalClose,
    });

  const onSubmit = (values: OrganizationVideoDuplicatePlaylistFormValues) => {
    duplicatePlaylistMutation(values);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={OrganizationVideoDuplicatePlaylistSchema}
      onSubmit={onSubmit}
      validateOnMount
    >
      {({ isSubmitting, setFieldValue, values, isValid }) => {
        const disabled = isSubmitting || !isValid;

        const onOrganizationVideoSelect = (
          video: OrganizationVideo,
          isSelected: boolean
        ) => {
          const videoId = video.organizationVideoId;

          const updatedIds = isSelected
            ? [...values.selectedOrganizationVideoIds, videoId]
            : values.selectedOrganizationVideoIds.filter(id => id !== videoId);

          setFieldValue('selectedOrganizationVideoIds', updatedIds);
        };

        const onSelectAll = (shouldSelectAll: boolean) => {
          setFieldValue(
            'selectedOrganizationVideoIds',
            shouldSelectAll ? videos.map(v => v.organizationVideoId) : []
          );
        };

        const selectedOrganizationVideos = videos.filter(v =>
          values.selectedOrganizationVideoIds.includes(v.organizationVideoId)
        );

        return (
          <Form>
            <Flex gap='16px'>
              <ParagraphNormal>New Playlist Settings</ParagraphNormal>

              <Flex flexDirection='row' gap='18px'>
                <FormikInputField
                  name='playlist'
                  placeholder='Add Playlist Name'
                  extendStyles={{ mainWrapper: { width: '290px' } }}
                  isRequired={false}
                />

                <FormikInputField
                  name='year'
                  type='number'
                  disabled={isSubmitting}
                  onChange={e =>
                    setFieldValue(
                      'year',
                      e.target.value ? parseInt(e.target.value, 10) : ''
                    )
                  }
                  extendStyles={{ mainWrapper: { width: '215px' } }}
                  isRequired={false}
                />
              </Flex>
            </Flex>
            <OrganizationVideoDuplicatePlaylistTable
              videos={videos}
              selectedOrganizationVideos={selectedOrganizationVideos}
              onOrganizationVideoSelect={onOrganizationVideoSelect}
              onSelectAll={onSelectAll}
            />
            <Flex
              flexDirection='row'
              justifyContent='flex-end'
              alignItems='center'
              gap='8px'
              margin='20px 0 0 0'
            >
              <Button
                text='Cancel'
                variant='secondary'
                disabled={isSubmitting}
                onClick={handleModalClose}
              />
              <Button
                text='Duplicate Playlist'
                disabled={disabled}
                type='submit'
              />
            </Flex>
          </Form>
        );
      }}
    </Formik>
  );
};
